import { useState } from "react";
import { Sparkles, Loader2, Plus, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { suggestIngredients } from "../lib/suggestions";
import USDAPickerDialog from "./USDAPickerDialog";
import { toast } from "sonner";

export const SuggestPanel = ({ dishName, onPickIngredient }) => {
  const [servings, setServings] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [addedIds, setAddedIds] = useState(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerContext, setPickerContext] = useState(null); // { idx, sugg }

  const fetchSuggestions = async () => {
    if (!dishName?.trim()) {
      toast.error("Donnez d'abord un nom à votre plat.");
      return;
    }
    setLoading(true);
    setSuggestions([]);
    setAddedIds(new Set());
    try {
      const data = await suggestIngredients(dishName.trim(), servings);
      setSuggestions(data.ingredients || []);
      if (!data.ingredients?.length) {
        toast.info("Aucune suggestion trouvée pour ce plat.");
      }
    } catch (e) {
      toast.error("Erreur IA. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  };

  const openPicker = (sugg, idx) => {
    setPickerContext({ sugg, idx });
    setPickerOpen(true);
  };

  const handlePickFromUSDA = (food) => {
    if (!pickerContext) return;
    const { sugg, idx } = pickerContext;
    try {
      onPickIngredient(food, sugg.quantity_g, sugg.name_fr);
      setAddedIds((prev) => new Set([...prev, idx]));
      setPickerOpen(false);
      setPickerContext(null);
    } catch (e) {
      toast.error("Erreur lors de l'ajout.");
    }
  };

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 border border-emerald-100 rounded-2xl p-6 sm:p-8 shadow-sm"
      data-testid="suggest-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-emerald-700 text-white grid place-items-center shrink-0">
            <Sparkles className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-heading text-lg font-bold text-stone-900">
              Suggestions IA d&apos;ingrédients
            </h3>
            <p className="text-xs text-stone-500 mt-0.5">
              Claude Sonnet 4.5 propose les ingrédients typiques pour votre plat.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
        <div className="flex-1">
          <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-1.5">
            Plat actuel
          </label>
          <div className="px-3 py-2 bg-white border border-stone-200 rounded-md text-sm text-stone-900 truncate min-h-[38px] flex items-center">
            {dishName?.trim() || <span className="text-stone-400">Saisissez un nom de plat ci-dessus</span>}
          </div>
        </div>
        <div className="sm:w-32">
          <label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500 mb-1.5">
            Portions
          </label>
          <Input
            type="number"
            min={1}
            max={20}
            value={servings}
            onChange={(e) => setServings(parseInt(e.target.value) || 1)}
            className="border-stone-200 focus-visible:ring-emerald-500"
            data-testid="servings-input"
          />
        </div>
        <Button
          onClick={fetchSuggestions}
          disabled={loading || !dishName?.trim()}
          className="bg-emerald-700 hover:bg-emerald-800 text-white font-semibold tracking-wide rounded-lg h-10 px-5 shrink-0"
          data-testid="suggest-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          Proposer
        </Button>
      </div>

      {suggestions.length > 0 && (
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="suggestions-list">
          {suggestions.map((s, i) => {
            const added = addedIds.has(i);
            return (
              <li
                key={i}
                className="flex items-center justify-between gap-3 px-3 py-2.5 bg-white border border-stone-200 rounded-lg hover:border-emerald-300 hover:shadow-sm transition-all"
                data-testid={`suggestion-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm text-stone-900 truncate">
                    {s.name_fr}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    {s.search_query_fr && (
                      <span className="font-mono text-emerald-700">🇨🇭 {s.search_query_fr}</span>
                    )}
                    {s.search_query_fr && s.search_query && (
                      <span className="text-stone-300">·</span>
                    )}
                    {s.search_query && (
                      <span className="font-mono text-stone-500">🇺🇸 {s.search_query}</span>
                    )}
                    <span className="text-stone-400">·</span>
                    <span className="font-semibold text-emerald-700">{s.quantity_g} g</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={added ? "outline" : "default"}
                  onClick={() => !added && openPicker(s, i)}
                  disabled={added}
                  className={
                    added
                      ? "h-8 px-3 text-emerald-700 border-emerald-200 bg-emerald-50 hover:bg-emerald-50"
                      : "h-8 px-3 bg-stone-900 hover:bg-stone-800 text-white"
                  }
                  data-testid={`add-suggestion-${i}`}
                >
                  {added ? (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1" />
                      Ajouté
                    </>
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Choisir source…
                    </>
                  )}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <USDAPickerDialog
        open={pickerOpen}
        onOpenChange={(o) => {
          setPickerOpen(o);
          if (!o) setPickerContext(null);
        }}
        initialQuery={pickerContext?.sugg?.search_query || ""}
        initialQueryFr={pickerContext?.sugg?.search_query_fr || ""}
        suggestionLabel={pickerContext?.sugg?.name_fr || ""}
        onPick={handlePickFromUSDA}
      />
    </div>
  );
};

export default SuggestPanel;
